import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

// Random pastel colors for post-it notes
const getRandomColor = () => {
  const colors = [
    '#FFF8DC', // Cornsilk
    '#FFEBCD', // BlanchedAlmond
    '#FFE4B5', // Moccasin
    '#FFDAB9', // PeachPuff
    '#EEE8AA', // PaleGoldenrod
    '#F0E68C', // Khaki
    '#E0FFFF', // LightCyan
    '#B0E0E6', // PowderBlue
    '#AFEEEE', // PaleTurquoise
    '#D8BFD8', // Thistle
    '#DDA0DD', // Plum
    '#FFCCCC', // Light Pink
    '#CCFFCC', // Light Green
    '#CCCCFF', // Light Blue
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Random rotation for post-it notes (-8 to 8 degrees)
const getRandomRotation = () => {
  return Math.floor(Math.random() * 16) - 8;
};

const NoteContainer = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  background-color: ${props => props.color || '#FFFF88'};
  padding: 20px;
  box-shadow: ${props => props.isActive 
    ? '10px 10px 25px rgba(0, 0, 0, 0.4)' 
    : '5px 5px 15px rgba(0, 0, 0, 0.2)'};
  transform: ${props => `rotate(${props.rotation}deg) ${props.isActive ? 'scale(1.05)' : 'scale(1)'}`};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: 'Indie Flower', cursive, sans-serif;
  
  &:hover {
    transform: ${props => `rotate(${props.rotation}deg) scale(1.05)`};
    box-shadow: 8px 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const MessageText = styled.div`
  flex: 1;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 10px;
`;

const AuthorText = styled.div`
  font-size: 12px;
  text-align: right;
  font-style: italic;
  margin-top: auto;
`;

const PostItNote = ({ message, author, x, y, index, rotation = 0 }) => {
  const [isActive, setIsActive] = useState(false);
  
  // Get a random color from the predefined colors
  const color = useMemo(() => {
    const colors = ['#FFFF88', '#FF9999', '#99FF99', '#9999FF', '#FFCC99'];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);
  
  // Handle click to bring note to front
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent click from propagating to board
    setIsActive(true);
  };
  
  // Handle click outside to reset active state
  const handleClickOutside = () => {
    setIsActive(false);
  };
  
  // Add event listener for clicks outside the note
  React.useEffect(() => {
    if (isActive) {
      const handleDocumentClick = (e) => {
        // If the click is outside this note, deactivate it
        if (!e.target.closest(`[data-note-id="${index}"]`)) {
          setIsActive(false);
        }
      };
      
      document.addEventListener('mousedown', handleDocumentClick);
      document.addEventListener('touchstart', handleDocumentClick);
      
      return () => {
        document.removeEventListener('mousedown', handleDocumentClick);
        document.removeEventListener('touchstart', handleDocumentClick);
      };
    }
  }, [isActive, index]);

  return (
    <div 
      style={{ 
        position: 'absolute', 
        left: x - 110, // Center the note (half of width)
        top: y - 110,  // Center the note (half of height)
        zIndex: isActive ? 2000 : index // Bring to front when active
      }}
      data-note-id={index}
      onClick={handleClick}
    >
      <NoteContainer 
        color={color} 
        rotation={rotation}
        isActive={isActive}
      >
        <MessageText>{message}</MessageText>
        <AuthorText>- {author || 'Anonymous'}</AuthorText>
      </NoteContainer>
    </div>
  );
};

export default PostItNote; 